import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import {
    applyBulkLevelAssign,
    fetchScopedAccounts,
    OrgAdminApiError,
    type ScopedAccountRow,
} from '@/api/orgAdmin';
import { filterParams } from './scopedAccountsQuery';
import type { ScopedAccountsVM } from './useScopedAccounts';

const SERVER_PAGE_SIZE = 100;
/** Same ceiling and same reason as `useScopedAccounts.MAX_SELECT_ALL_PAGES`. */
const MAX_SWEEP_PAGES = 20;

/**
 * EAA-1839 (frame 35a): the "Chọn Level" chip's own popover — assigning a Level to
 * ONE row without leaving the table, plus the frame's "Áp dụng cho cả N tài khoản
 * chưa có Level" escalation.
 *
 * WHY THIS IS NOT `useBulkLevelAssign` WITH ONE ROW. That hook drives the frame-36
 * dialog: pick a level, run `bulk-preview`, show the three-way split and the
 * per-app impact, then apply. All of that is the point when the target set is a
 * filtered selection whose current levels the operator cannot see. Here the target
 * is a single row whose Level cell the operator is looking at and which is
 * empty — there is nothing to preview, so a preview step would be a modal
 * standing between a click and its obvious result.
 *
 * The WRITE is still `bulk-apply`, not `POST .../assignments`. A row in the
 * "Chưa có Level" state is an ACTIVE assignment with `level_id = null`, so creating
 * a second assignment would leave two active grants for the same person in the same
 * đơn vị; `bulk_apply` re-derives its own buckets from `user_subs` and puts such a
 * row in `changed`, revoking the level-less grant before creating the new one.
 * `bulk-apply` with a one-element list is therefore the correct single-row write,
 * not a bulk endpoint pressed into service.
 *
 * "ÁP DỤNG CHO CẢ N" sweeps the `issue=no_level` pages first, because `bulk-apply`
 * takes subs and the console holds only the current page. The sweep is capped like
 * every other page walk in this screen and reports what it covered.
 *
 * WHAT IS INERT: the frame's "Gửi email thông báo cho người dùng" checkbox. Neither
 * bulk endpoint's serializer accepts a notify flag, and no separate notify endpoint
 * exists — a ticked box that sends nothing is worse than a box that says so.
 */
export function useInlineLevelPicker(vm: ScopedAccountsVM) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();

    const [row, setRow] = useState<ScopedAccountRow | null>(null);
    const [levelId, setLevelId] = useState('');
    const [query, setQuery] = useState('');
    const [applyToAll, setApplyToAll] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const open = useCallback((target: ScopedAccountRow) => {
        setRow(target);
        setLevelId(target.level_id ?? '');
        setQuery('');
        setApplyToAll(false);
        setError(null);
    }, []);

    const close = useCallback(() => setRow(null), []);

    /*
     * EAA-1817: the list is `vm.activeLevels`, which is now fetched with TREE scope
     * (see `useScopedAccounts`) — so it already contains every Level this row can
     * legally be given, including an ancestor đơn vị's.
     *
     * This briefly had its OWN per-row fetch of the row's owning org, added when the
     * only complaint was that a child's row was offered the parent's Levels and
     * nothing else. Widening the shared query made that redundant, and leaving two
     * sources feeding one picker is how they drift apart.
     */
    const availableLevels = vm.activeLevels;

    const levels = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return availableLevels;
        return availableLevels.filter(
            (level) =>
                level.name.toLowerCase().includes(needle) ||
                level.role_key.toLowerCase().includes(needle)
        );
    }, [availableLevels, query]);

    /** Every sub in the branch that currently has no Level — capped, and says so. */
    const collectNoLevelSubs = useCallback(async (): Promise<{ subs: string[]; capped: boolean }> => {
        if (!vm.orgId || !accessToken) return { subs: [], capped: false };
        const subs = new Set<string>();
        let page = 1;
        let hitCap = false;
        for (;;) {
            const res = await fetchScopedAccounts(config.apiBase, accessToken, vm.orgId, {
                ...filterParams({ ...vm.filters, issue: 'no_level' }),
                issue: 'no_level',
                page,
                pageSize: SERVER_PAGE_SIZE,
            });
            // Cross-đơn-vị rows are excluded for exactly the reason
            // `useScopedAccounts.scopeSelectedRows` excludes them: `bulk_apply` is
            // deliberately single-org, so a descendant's row would gain a SECOND
            // active grant in the parent instead of changing the one on screen.
            for (const entry of res.results) {
                if (entry.organization_id === vm.orgId) subs.add(entry.user_sub);
            }
            if (page >= res.total_pages) break;
            if (page >= MAX_SWEEP_PAGES) {
                hitCap = true;
                break;
            }
            page += 1;
        }
        return { subs: [...subs], capped: hitCap };
    }, [vm.orgId, vm.filters, accessToken, config.apiBase]);

    const mutation = useMutation({
        mutationFn: async () => {
            if (!row || !levelId) throw new Error('unreachable');
            const targets = applyToAll
                ? await collectNoLevelSubs()
                : { subs: [row.user_sub], capped: false };
            if (targets.subs.length === 0) {
                return { applied: [], skipped: [], capped: targets.capped };
            }
            const result = await applyBulkLevelAssign(
                config.apiBase,
                accessToken as string,
                // The ROW's org, not the scope org — with children in scope a row can
                // belong to a descendant, and `bulk_apply` resolves against the org in
                // the path (see `useScopedAccountWrites.revokeMutation` for the same rule).
                applyToAll ? (vm.orgId as string) : row.organization_id,
                { userSubs: targets.subs, levelId }
            );
            return { ...result, capped: targets.capped };
        },
        onSuccess: (result) => {
            close();
            vm.invalidateList();
            // EAA-1817: applying nothing is not a success. `bulk_apply` reports a
            // rejected level (or an already-held one) by returning every id in
            // `skipped` rather than raising, so a handler that only reads
            // `applied` announced "Đã gán Level cho 0 tài khoản." as good news —
            // which is how the wrong-org bug above stayed invisible.
            // `useBulkLevelAssign` already draws this distinction; this path did not.
            if (result.applied.length === 0) {
                toast.error(
                    t('console.hierarchy.scopedAccounts.inlineLevel.nothingAppliedToast', {
                        count: result.skipped.length,
                    })
                );
                return;
            }
            toast.success(
                t('console.hierarchy.scopedAccounts.inlineLevel.successToast', {
                    count: result.applied.length,
                })
            );
            if (result.capped) {
                toast.error(
                    t('console.hierarchy.scopedAccounts.inlineLevel.cappedToast', {
                        max: (MAX_SWEEP_PAGES * SERVER_PAGE_SIZE).toLocaleString('vi-VN'),
                    })
                );
            }
        },
        onError: (err: unknown) => {
            const message =
                err instanceof OrgAdminApiError
                    ? err.message
                    : t('console.hierarchy.scopedAccounts.inlineLevel.errorFallback');
            setError(message);
            toast.error(message);
        },
    });

    const submit = useCallback(() => {
        if (!levelId) {
            setError(t('console.hierarchy.scopedAccounts.inlineLevel.validationLevelRequired'));
            return;
        }
        setError(null);
        mutation.mutate();
    }, [levelId, mutation, t]);

    /** Only real when the row belongs to the scope org — see `collectNoLevelSubs`. */
    const canApplyToAll = Boolean(row && row.organization_id === vm.orgId);

    return {
        row,
        open,
        close,
        levels,
        /** Kept in the VM shape: the shared levels query can still be in flight. */
        levelsLoading: vm.levelsLoading,
        levelId,
        setLevelId,
        query,
        setQuery,
        applyToAll,
        setApplyToAll,
        canApplyToAll,
        noLevelCount: vm.noLevelCount,
        error,
        submit,
        busy: mutation.isPending,
    };
}

export type InlineLevelPickerVM = ReturnType<typeof useInlineLevelPicker>;
